/*!
=========================================================
* Regiform IMS Frontend - ConfigurableFilters Component
=========================================================
* Configurable filter component with Ant Design styling
* Maintains focus stability while providing flexible configuration
=========================================================
*/
import React, { useCallback, useMemo } from "react";
import { Space, Select, Card, Checkbox, Col, Row, DatePicker } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import "./ConfigurableFilters.css";
import Button from "../Button";
import { Input } from "../non-form-input";

const { Option } = Select;

const ConfigurableFilters = ({
  filters = [],
  onRefresh,
  onClearFilters,
  isLoading = false,
  title,
  className = "",
  showCard = true,
  size = "middle",
  onMultiSelect,
  multiSelect,
  extra,
  ...otherProps
}) => {
  // Render individual filter based on configuration
  const renderFilter = useCallback(
    (filter, index) => {
      const {
        type,
        placeholder,
        value,
        onChange,
        options = [],
        prefix,
        suffix,
        allowClear = true,
        style = {},
        className: filterClassName = "",
        disabled = false,
        size: filterSize,
        ...filterProps
      } = filter;

      const key = filter.id || filter.key || `filter-${type}-${index}`;

      switch (type) {
        case "INPUT":
          return (
            <Input
              key={key}
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              prefix={prefix}
              suffix={suffix}
              allowClear={allowClear}
              disabled={disabled}
              size={filterSize || size}
              style={style}
              className={`configurable-filter-input ${filterClassName}`}
              {...filterProps}
            />
          );

        case "SEARCH":
          return (
            <Input.Search
              key={key}
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              onSearch={filter.onSearch}
              allowClear={allowClear}
              disabled={disabled}
              size={filterSize || size}
              style={style}
              className={`configurable-filter-search ${filterClassName}`}
              {...filterProps}
            />
          );

        case "SELECT":
        case "DROPDOWN":
          return (
            <Select
              key={key}
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              allowClear={allowClear}
              disabled={disabled}
              size={filterSize || size}
              style={style}
              className={`configurable-filter-select ${filterClassName}`}
              options={options}
              {...filterProps}
            />
          );

        case "DATE_RANGE":
          return (
            <DatePicker.RangePicker
              key={key}
              value={value}
              onChange={onChange}
              allowClear={allowClear}
              disabled={disabled}
              size={filterSize || size}
              style={style}
              className={`configurable-filter-date-range ${filterClassName}`}
              {...filterProps}
            />
          );

        case "BUTTON":
          return (
            <Button
              key={key}
              type={filter.buttonType || "default"}
              onClick={filter.onClick}
              disabled={disabled || isLoading}
              size={filterSize || size}
              icon={filter.icon}
              className={`configurable-filter-button ${filterClassName}`}
              {...filterProps}
            >
              {filter.label}
            </Button>
          );

        default:
          console.warn(`Unsupported filter type: ${type}`);
          return null;
      }
    },
    [size, isLoading]
  );

  // Memoize the filters section to prevent unnecessary re-renders
  const filtersSection = useMemo(() => {
    if (filters.length === 0) return null;

    return (
      <Row justify="space-between" align="middle" style={{ width: "100%" }}>
        <Col>
          <Space size="middle" wrap className="configurable-filters-space">
            {filters.map((filter, index) => renderFilter(filter, index))}

            {/* {onClearFilters && (
          <Button
            size={size}
            onClick={onClearFilters}
            className="configurable-filters-clear"
          >
            Clear Filters
          </Button>
        )} */}

            {onRefresh && (
              <Button
                type="text"
                size={size}
                icon={<ReloadOutlined />}
                onClick={onRefresh}
                loading={isLoading}
                className="configurable-filters-refresh"
              >
                Refresh
              </Button>
            )}
          </Space>
        </Col>

        <Col>
          <Space size="middle" wrap className="configurable-filters-space">
            {onMultiSelect && (
              <Checkbox
                value={multiSelect}
                onChange={onMultiSelect}
                className="configurable-filters-multi-select"
              >
                Multi Select
              </Checkbox>
            )}
            {extra}
          </Space>
        </Col>
      </Row>
    );
  }, [filters, renderFilter, onRefresh, isLoading, size, multiSelect, onMultiSelect, extra]);

  // Render content
  const content = (
    <div className={`configurable-filters ${className}`} {...otherProps}>
      {title && <div className="configurable-filters-title">{title}</div>}
      {filtersSection}
    </div>
  );

  // Wrap in Card if requested
  if (showCard) {
    return <Card className="configurable-filters-card">{content}</Card>;
  }

  return content;
};

ConfigurableFilters.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(["INPUT", "SEARCH", "SELECT", "DROPDOWN", "DATE_RANGE", "BUTTON"])
        .isRequired,
      id: PropTypes.string,
      key: PropTypes.string,
      placeholder: PropTypes.string,
      value: PropTypes.any,
      onChange: PropTypes.func,
      onSearch: PropTypes.func, // For SEARCH type
      onClick: PropTypes.func, // For BUTTON type
      label: PropTypes.string, // For BUTTON type
      buttonType: PropTypes.string, // For BUTTON type
      icon: PropTypes.node, // For BUTTON type
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.any.isRequired,
          label: PropTypes.string.isRequired,
        })
      ), // For SELECT/DROPDOWN type
      prefix: PropTypes.node,
      suffix: PropTypes.node,
      allowClear: PropTypes.bool,
      disabled: PropTypes.bool,
      size: PropTypes.oneOf(["small", "middle", "large"]),
      style: PropTypes.object,
      className: PropTypes.string,
    })
  ).isRequired,
  onRefresh: PropTypes.func,
  onClearFilters: PropTypes.func,
  isLoading: PropTypes.bool,
  title: PropTypes.string,
  className: PropTypes.string,
  showCard: PropTypes.bool,
  size: PropTypes.oneOf(["small", "middle", "large"]),
  extra: PropTypes.node,
};

export default ConfigurableFilters;
